import { PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID } from '@billing/validation/tariff';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Paper,
  Popover,
  SegmentedControl,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from '@refinedev/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconChevronDown,
  IconX,
} from '@tabler/icons-react';
import type { Payor } from 'interfaces';
import { useMemo, useState } from 'react';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';
import {
  ALL_CLAIM_TAB,
  useClaimTabFilterStore,
} from 'store/ClaimTabFilterStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import {
  formatDateFilterToken,
  formatDateOnly,
  formatDateRange,
  getDatePickerPresetRanges,
  nowISODate,
} from 'utils/dateUtils';
import { providerClaimFilterStyles } from './ProviderClaimsStyles';

const DATE_FILTERS = [
  { id: 'serviceStart', key: 'serviceDate' },
  { id: 'submissions.submittedAt', key: 'billedDate' },
  { id: 'remittances.parsedAt', key: 'paidDate' },
] as const;
const isProviderDateFilter = (id: string): boolean =>
  id === PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID ||
  DATE_FILTERS.some((filter) => filter.id === id);
const PAYOR_FILTER_ID = 'payorID';
const PAYOR_TYPE_FILTER_ID = 'payor.type';

type DateRange = [string, string];

const formatProviderDateRange = (
  [start, end]: DateRange,
  timezone: string
): string => {
  if (start === end) return formatDateOnly(start, { timezone });
  return formatDateRange(start, end, { timezone });
};

const getFilterValues = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  return value ? [String(value)] : [];
};

const getDateRange = (value: unknown): DateRange => {
  if (!Array.isArray(value)) return ['', ''];
  return [
    formatDateFilterToken(value[0]) ?? '',
    formatDateFilterToken(value[1]) ?? '',
  ];
};

interface ProviderNeedsYouProps {
  count: number;
  variant: 'banner' | 'filter';
}

export const ProviderNeedsYou = ({ count, variant }: ProviderNeedsYouProps) => {
  const { translate } = useTranslation();
  const isDark = useComputedColorScheme('dark') === 'dark';
  const bannerAppearance = isDark
    ? providerClaimFilterStyles.darkNeedsYou
    : undefined;
  const {
    providerNeedsAction,
    toggleProviderNeedsAction,
    clearSubStatuses,
    setActiveTab,
  } = useClaimTabFilterStore();
  const { pagination, setColumnFilters, setPageIndex } =
    useClaimPaginationStore();

  const toggleNeedsAction = () => {
    if (count === 0 && !providerNeedsAction) return;

    if (!providerNeedsAction) {
      setActiveTab(ALL_CLAIM_TAB);
      clearSubStatuses();
      setColumnFilters(
        pagination.columnFilters.filter(
          (filter) =>
            filter.id !== PAYOR_FILTER_ID && filter.id !== PAYOR_TYPE_FILTER_ID
        )
      );
    }
    toggleProviderNeedsAction();
    setPageIndex(0);
  };

  const countLabel = translate(
    count === 1
      ? 'pages:claims.providerFilters.needsYou.singleTitle'
      : 'pages:claims.providerFilters.needsYou.title',
    { count },
    count === 1
      ? '1 claim is waiting on you'
      : `${count} claims are waiting on you`
  );

  if (variant === 'banner') {
    if (count === 0) return null;
    return (
      <Paper
        data-testid="provider-needs-you-banner"
        style={{
          ...providerClaimFilterStyles.needsYouBanner,
          ...bannerAppearance?.banner,
        }}
      >
        <Box
          style={{
            ...providerClaimFilterStyles.needsYouIconContainer,
            ...bannerAppearance?.icon,
          }}
        >
          <IconAlertTriangle size={18} color="white" />
          <Badge
            style={{
              ...providerClaimFilterStyles.needsYouCountBadge,
              ...bannerAppearance?.badge,
            }}
          >
            {count}
          </Badge>
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="sm" c="red.8" style={bannerAppearance?.title}>
            {countLabel}
          </Text>
          <Text size="xs" c="dimmed" lh={1.4} style={bannerAppearance?.body}>
            {translate(
              'pages:claims.providerFilters.needsYou.body',
              'Your biller has asked you for something — a missing detail, a document, or a confirmation. These will not be submitted until you reply.'
            )}
          </Text>
        </Box>
        <Button
          color="red"
          size="xs"
          style={bannerAppearance?.button}
          onClick={toggleNeedsAction}
          rightSection={<IconArrowRight size={15} />}
        >
          {translate(
            providerNeedsAction
              ? 'pages:claims.providerFilters.needsYou.showing'
              : 'pages:claims.providerFilters.needsYou.review',
            providerNeedsAction ? 'View all claims' : 'Review now'
          )}
        </Button>
      </Paper>
    );
  }

  return (
    <Button
      variant={providerNeedsAction ? 'filled' : 'default'}
      color={count > 0 ? 'red' : 'gray'}
      size="xs"
      disabled={count === 0 && !providerNeedsAction}
      aria-pressed={providerNeedsAction}
      style={providerClaimFilterStyles.getNeedsYouFilter(providerNeedsAction)}
      onClick={toggleNeedsAction}
      leftSection={<IconAlertTriangle size={15} />}
      rightSection={
        providerNeedsAction ? <IconX size={13} aria-hidden /> : undefined
      }
      data-testid="provider-needs-you-filter"
    >
      {translate(
        'pages:claims.providerFilters.needsYou.filter',
        { count },
        `Needs you · ${count}`
      )}
    </Button>
  );
};

interface ProviderClaimSelectorsProps {
  payors: Payor[];
  payorTariffCounts: Record<string, number>;
}

export const ProviderClaimSelectors = ({
  payors,
  payorTariffCounts,
}: ProviderClaimSelectorsProps) => {
  const { translate } = useTranslation();
  const { timezone } = useCurrentSiteStore();
  const isWideScreen = useMediaQuery('(min-width: 900px)');
  const { pagination, setColumnFilters, setPageIndex } =
    useClaimPaginationStore();
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [draftDateRange, setDraftDateRange] = useState<DateRange>(['', '']);

  const [selectedDateFilterID, setSelectedDateFilterID] =
    useState<string>('serviceStart');
  const [draftDateFilterID, setDraftDateFilterID] = useState('serviceStart');
  const activeDateFilter = pagination.columnFilters.find((filter) =>
    isProviderDateFilter(filter.id)
  );
  const activeDateFilterID = activeDateFilter?.id ?? selectedDateFilterID;
  const activeDateConfig =
    DATE_FILTERS.find(
      (filter) =>
        filter.id ===
        (activeDateFilterID === PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID
          ? 'submissions.submittedAt'
          : activeDateFilterID)
    ) ?? DATE_FILTERS[0];
  const draftDateConfig =
    DATE_FILTERS.find((filter) => filter.id === draftDateFilterID) ??
    DATE_FILTERS[0];
  const activeDateRange = getDateRange(activeDateFilter?.value);

  const changeDatePickerOpened = (opened: boolean) => {
    if (opened) {
      setDraftDateRange(activeDateRange);
      setDraftDateFilterID(
        activeDateFilterID === PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID
          ? 'submissions.submittedAt'
          : activeDateFilterID
      );
    }
    setDatePickerOpened(opened);
  };
  const payorFilter = pagination.columnFilters.find(
    (filter) => filter.id === PAYOR_FILTER_ID
  );
  const payorTypeFilter = pagination.columnFilters.find(
    (filter) => filter.id === PAYOR_TYPE_FILTER_ID
  );
  const selectedPayorIDs = payorFilter
    ? getFilterValues(payorFilter.value)
    : payors
        .filter((payor) => payor.type === payorTypeFilter?.value)
        .map((payor) => payor.id);

  const updateFilter = (id: string, value: unknown) => {
    const otherFilters = pagination.columnFilters.filter(
      (filter) =>
        filter.id !== id &&
        !(id === PAYOR_FILTER_ID && filter.id === PAYOR_TYPE_FILTER_ID)
    );
    const nextFilters = value ? [...otherFilters, { id, value }] : otherFilters;
    setColumnFilters(nextFilters);
    setPageIndex(0);
  };

  const datePresets = useMemo(() => {
    const ranges = getDatePickerPresetRanges({ timezone });
    return [
      {
        key: 'yearToDate',
        value: ranges.yearToDate,
        fallback: 'Year to date',
      },
      {
        key: 'lastMonth',
        value: ranges.lastMonth,
        fallback: 'Last month',
      },
      {
        key: 'lastQuarter',
        value: ranges.lastQuarter,
        fallback: 'Last quarter',
      },
      {
        key: 'lastFiscalYear',
        value: ranges.lastFiscalYear,
        fallback: 'Last fiscal year',
      },
      { key: 'allTime', value: ['', ''] as DateRange, fallback: 'All time' },
      {
        key: 'customRange',
        value: ['', ''] as DateRange,
        fallback: 'Custom range',
      },
    ];
  }, [timezone]);

  const filteredPayors = payors.filter(
    (payor) => payorTariffCounts[payor.id] > 0
  );
  const selectedPayorNames = filteredPayors
    .filter((payor) => selectedPayorIDs.includes(payor.id))
    .map((payor) => payor.name);
  const getPayorButtonLabel = () => {
    if (selectedPayorNames.length === 0) {
      return translate('pages:claims.providerFilters.payor.all', 'All payors');
    }
    if (selectedPayorNames.length === 1) return selectedPayorNames[0];
    return translate(
      'pages:claims.providerFilters.payor.selected',
      { count: selectedPayorNames.length },
      `${selectedPayorNames.length} payors`
    );
  };
  const payorButtonLabel = getPayorButtonLabel();
  const dateLabel =
    activeDateRange[0] && activeDateRange[1]
      ? translate(
          `pages:claims.providerFilters.${activeDateConfig.key}.range`,
          {
            range: formatProviderDateRange(activeDateRange, timezone),
          }
        )
      : translate(`pages:claims.providerFilters.${activeDateConfig.key}.all`);

  const applyDateRange = () => {
    const otherFilters = pagination.columnFilters.filter(
      (filter) => !isProviderDateFilter(filter.id)
    );
    const hasCompleteRange = draftDateRange[0] && draftDateRange[1];
    const appliedDateFilterID =
      activeDateFilterID === PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID &&
      draftDateFilterID === 'submissions.submittedAt'
        ? PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID
        : draftDateFilterID;
    setColumnFilters(
      hasCompleteRange
        ? [...otherFilters, { id: appliedDateFilterID, value: draftDateRange }]
        : otherFilters
    );
    setSelectedDateFilterID(draftDateFilterID);
    setPageIndex(0);
    setDatePickerOpened(false);
  };

  const togglePayor = (payorID: string) => {
    const nextPayorIDs = selectedPayorIDs.includes(payorID)
      ? selectedPayorIDs.filter((id) => id !== payorID)
      : [...selectedPayorIDs, payorID];
    updateFilter(
      PAYOR_FILTER_ID,
      nextPayorIDs.length > 0 ? nextPayorIDs : undefined
    );
  };

  return (
    <Group gap="xs" wrap="wrap">
      <Popover
        opened={datePickerOpened}
        onChange={changeDatePickerOpened}
        position="bottom-start"
        shadow="xl"
      >
        <Popover.Target>
          <Button
            variant="default"
            size="xs"
            rightSection={<IconChevronDown size={14} />}
            onClick={() => changeDatePickerOpened(!datePickerOpened)}
            data-active={Boolean(activeDateRange[0] && activeDateRange[1])}
            style={providerClaimFilterStyles.selectorButton}
          >
            {dateLabel}
          </Button>
        </Popover.Target>
        <Popover.Dropdown p={0} style={providerClaimFilterStyles.dateDropdown}>
          <Flex align="stretch">
            <Stack gap={2} style={providerClaimFilterStyles.presetColumn}>
              {datePresets.map((preset) => (
                <Button
                  key={preset.key}
                  variant="subtle"
                  color="gray"
                  size="xs"
                  justify="flex-start"
                  onClick={() => setDraftDateRange(preset.value)}
                >
                  {translate(
                    `pages:claims.providerFilters.serviceDate.${preset.key}`,
                    preset.fallback
                  )}
                </Button>
              ))}
            </Stack>
            <Stack gap="sm" p="sm">
              <Group gap="xs">
                <Text size="xs" fw={700} c="dimmed">
                  {translate('pages:claims.providerFilters.dateType')}
                </Text>
                <SegmentedControl
                  size="xs"
                  value={draftDateFilterID}
                  onChange={setDraftDateFilterID}
                  aria-label={translate(
                    'pages:claims.providerFilters.dateType'
                  )}
                  data={DATE_FILTERS.map((filter) => ({
                    value: filter.id,
                    label: translate(
                      `pages:claims.providerFilters.${filter.key}.label`
                    ),
                  }))}
                />
              </Group>
              <DatePicker
                type="range"
                value={draftDateRange}
                onChange={(value) => setDraftDateRange(value as DateRange)}
                numberOfColumns={isWideScreen ? 2 : 1}
                maxDate={nowISODate(timezone)}
                allowSingleDateInRange
              />
              <Group justify="space-between" gap="md" wrap="nowrap">
                <Text size="xs" c="dimmed">
                  {draftDateRange[0] && !draftDateRange[1]
                    ? translate(
                        'pages:claims.providerFilters.serviceDate.pickEnd',
                        'Pick the end of the range'
                      )
                    : translate(
                        `pages:claims.providerFilters.${draftDateConfig.key}.help`
                      )}
                </Text>
                <Group gap="xs" wrap="nowrap">
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => setDraftDateRange(['', ''])}
                  >
                    {translate('pages:claims.providerFilters.clear', 'Clear')}
                  </Button>
                  <Button
                    size="xs"
                    onClick={applyDateRange}
                    disabled={Boolean(draftDateRange[0] && !draftDateRange[1])}
                  >
                    {translate('pages:claims.providerFilters.done', 'Done')}
                  </Button>
                </Group>
              </Group>
            </Stack>
          </Flex>
        </Popover.Dropdown>
      </Popover>

      <Popover position="bottom-start" shadow="lg" width={258}>
        <Popover.Target>
          <Button
            variant="default"
            size="xs"
            rightSection={<IconChevronDown size={14} />}
            data-active={selectedPayorIDs.length > 0}
            style={providerClaimFilterStyles.selectorButton}
          >
            {payorButtonLabel}
          </Button>
        </Popover.Target>
        <Popover.Dropdown p={6}>
          <Text style={providerClaimFilterStyles.dropdownLabel}>
            {translate('pages:claims.providerFilters.payor.heading', 'Payor')}
          </Text>
          <Stack gap={1}>
            {filteredPayors.map((payor) => (
              <Checkbox
                key={payor.id}
                checked={selectedPayorIDs.includes(payor.id)}
                onChange={() => togglePayor(payor.id)}
                label={
                  <Group justify="space-between" wrap="nowrap" gap="md">
                    <Text size="sm">{payor.name}</Text>
                    <Text size="xs" c="dimmed">
                      {payorTariffCounts[payor.id]}
                    </Text>
                  </Group>
                }
                styles={providerClaimFilterStyles.payorCheckbox}
              />
            ))}
            {filteredPayors.length === 0 ? (
              <Text size="sm" c="dimmed" p="xs">
                {translate(
                  'pages:claims.providerFilters.payor.empty',
                  'No payors available'
                )}
              </Text>
            ) : null}
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              justify="flex-start"
              mt={4}
              onClick={() => updateFilter(PAYOR_FILTER_ID, undefined)}
            >
              {translate(
                'pages:claims.providerFilters.payor.clear',
                'Clear selection'
              )}
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};
